import { Chip } from '@mui/material'
import type { GridColDef } from '@mui/x-data-grid';
import { getGridSingleSelectOperators } from '@mui/x-data-grid'

import { isTestAppointment } from '../../../appointment.utils.ts'
import type { Appointment, AppointmentStatus } from '../../../types.ts'

import CommentCell from './CommentCell'
import ExternalLinkCell from './ExternalLinkCell'
import FunnelFilterHeader from './FunnelFilterHeader'
import { LinkCell } from './LinkCell/LinkCell.tsx'
import { NameCell } from './NameCell/NameCell.tsx'
import { ProductCell } from './ProductCell/ProductCell.tsx'
import { StatusCell } from './StatusCell/StatusCell.tsx'
import SubtypeFilterHeader from './SubtypeFilterHeader'
import TypeFilterHeader from './TypeFilterHeader'

import { getFormattedDateAndTime } from '@/shared/utils'

export const FUNNEL_LABELS: Record<string, string> = {
  admin: 'Admin',
  event: 'Event',
  scout: 'Privat',
  unknown: 'Unknown',
}

interface Props {
  funnelFilter?: string | undefined
  currentStatus?: AppointmentStatus
  typeFilter?: string | undefined
  onTypeFilterChange?: (type: string | undefined) => void
  subtypeFilter?: string[] | undefined
  onSubtypeFilterChange?: (subtypes: string[] | undefined) => void
  onFunnelFilterChange?: (funnel: string | undefined) => void
}

export const getColumns = ({
  currentStatus,
  funnelFilter,
  typeFilter,
  onTypeFilterChange,
  subtypeFilter,
  onSubtypeFilterChange,
  onFunnelFilterChange,
}: Props): GridColDef<Appointment>[] => {
  const baseColumns: GridColDef<Appointment>[] = [
    {
      field: 'customerName',
      headerName: 'Customer',
      width: currentStatus === 'new' ? 150 : 150,
      sortable: false,
      filterable: false,
      renderCell: ({ value }) => <NameCell value={value as string} />,
    },
    {
      field: 'customerAddress',
      headerName: 'Address',
      width: currentStatus === 'new' ? 230 : 160,
      sortable: false,
      filterable: false,
      renderCell: ({ value }) => {
        if (!value || value === '-') return '-'
        return (
          <ExternalLinkCell
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(value)}`}
            value={value}
          />
        )
      },
    },
  ]

  // Add status column when 'all' tab is selected
  if (currentStatus === 'all') {
    baseColumns.push({
      field: 'status',
      headerName: 'Status',
      width: 130,
      sortable: false,
      filterable: false,
      renderCell: ({ value }) => <StatusCell value={value as AppointmentStatus} />,
    })
  }

  const statusSpecificColumns: GridColDef<Appointment>[] = []

  if (currentStatus === 'new') {
    statusSpecificColumns.push(
      {
        field: 'scoutCount',
        headerName: 'Scout Assignments',
        width: 180,
        sortable: false,
        filterable: false,
        renderCell: params => {
          if (!params || !params.row) return '-'
          const count = params.row.scoutCount || 0
          const emoji = count > 0 ? '✅' : '❌'
          return `${emoji} ${count}`
        },
      },
      {
        field: 'scoutNotificationSent',
        headerName: 'Scout Notification Time',
        width: 180,
        sortable: false,
        filterable: false,
        renderCell: ({ value }) => getFormattedDateAndTime(value as string) || '-',
      }
    )
  } else if (currentStatus === 'all') {
    // Show essential columns for 'all' view
    statusSpecificColumns.push(
      {
        field: 'scoutName',
        headerName: 'Scout',
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: ({ row, value }) => (
          <LinkCell href={row.scoutUUID ? `/scouts/show/${row.scoutUUID}` : ''} value={value} />
        ),
      },
      {
        field: 'scheduleStartTime',
        headerName: 'Schedule Time',
        width: 180,
        sortable: true,
        filterable: false,
        renderCell: ({ value }) => getFormattedDateAndTime(value as string) || '-',
      }
    )
  } else {
    statusSpecificColumns.push(
      {
        field: 'scoutName',
        headerName: 'Scout',
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: ({ row, value }) => (
          <LinkCell href={row.scoutUUID ? `/scouts/show/${row.scoutUUID}` : ''} value={value} />
        ),
      },
      {
        field: 'scheduleStartTime',
        headerName: 'Schedule Time',
        width: 180,
        sortable: true,
        filterable: false,
        renderCell: ({ value }) => getFormattedDateAndTime(value as string) || '-',
      },
      {
        field: 'dataCollected',
        headerName: 'Data Collected',
        width: 130,
        sortable: false,
        filterable: false,
        renderCell: ({ value }) => (
          <Chip label={value ? 'Yes' : 'No'} color={value ? 'success' : 'default'} size="small" />
        ),
      }
    )
  }

  const commonColumns: GridColDef<Appointment>[] = [
    {
      field: 'type',
      headerName: 'Type',
      width: 150,
      sortable: false,
      filterable: false,
      renderHeader: () => (
        <TypeFilterHeader value={typeFilter} onChange={val => onTypeFilterChange?.(val)} />
      ),
      renderCell: params => {
        const type = params.value as string
        let displayType = ''
        let chipColor: 'primary' | 'secondary' | 'default' = 'default'

        if (type === 'isfp') {
          displayType = 'iSFP'
          chipColor = 'primary'
        } else if (type) {
          displayType = type.charAt(0).toUpperCase() + type.slice(1)
        }
        return <Chip label={displayType} color={chipColor} size="small" />
      },
    },
    {
      field: 'funnel',
      headerName: 'Funnel',
      width: 130,
      sortable: false,
      filterable: false,
      renderHeader: () => (
        <FunnelFilterHeader value={funnelFilter} onChange={val => onFunnelFilterChange?.(val)} />
      ),
      renderCell: params => {
        const funnel = params.value as string | null | undefined
        if (!funnel) return null
        return (
          <Chip
            label={FUNNEL_LABELS[funnel] ?? funnel}
            color={funnel === 'scout' ? 'secondary' : 'default'}
            size="small"
          />
        )
      },
    },
    {
      field: 'subtypes',
      headerName: 'Product',
      width: 160,
      sortable: false,
      filterable: false,
      renderHeader: () => (
        <SubtypeFilterHeader value={subtypeFilter} onChange={val => onSubtypeFilterChange?.(val)} />
      ),
      renderCell: params => {
        return <ProductCell value={params.value} row={params.row} />
      },
    },
    {
      field: 'createdAt',
      headerName: 'Creation Time',
      width: 150,
      sortable: true,
      filterable: false,
      renderCell: ({ value }) => getFormattedDateAndTime(value as string),
    },
    {
      field: 'environment',
      headerName: 'Environment',
      width: 140,
      sortable: false,
      filterable: true,
      type: 'singleSelect',
      valueOptions: [
        { value: 'live', label: 'Live' },
        { value: 'test', label: 'Test' },
      ],
      filterOperators: getGridSingleSelectOperators().filter(operator => operator.value === 'is'),
      renderCell: params => {
        const appointment = params.row
        const isTest = isTestAppointment(appointment)
        return (
          <Chip
            label={isTest ? 'Test' : 'Live'}
            size="small"
            variant={isTest ? 'outlined' : undefined}
            color={isTest ? 'default' : 'success'}
            sx={{
              opacity: isTest ? 0.6 : 1,
              fontSize: '0.75rem',
              fontWeight: isTest ? 'normal' : 'bold',
            }}
          />
        )
      },
    },
    {
      field: 'salesComment',
      headerName: 'Comment',
      width: 450,
      sortable: false,
      filterable: false,
      renderCell: ({ value, row }) => <CommentCell value={value} row={row} />,
    },
  ]

  return [...baseColumns, ...statusSpecificColumns, ...commonColumns]
}
